/**
 * Shared Navigation Component
 * Injects navbar/mobile-menu/footer HTML and handles wallet auth state across all pages
 */

import { XamanAuth } from './xaman-auth';
import { showError } from './modal';
import { API_BASE_URL } from './config';

// --- Template helpers ---

function getActivePage(): string {
  const path = window.location.pathname;
  const page = path.split('/').pop()?.replace('.html', '') || 'index';
  if (page === '' || page === '/') return 'index';
  return page;
}

function getNavbarHTML(): string {
  const page = getActivePage();
  const isIndex = page === 'index';
  const homeHref = isIndex ? '#home' : 'index.html#home';
  const ac = (p: string) => page === p ? ' class="active"' : '';

  return `<div class="nav-container">
      <div class="logo">
        <img src="img/logo.png" alt="AOX Logo" height="50">
        <span>AOX</span>
      </div>
      <div class="nav-links">
        <a href="${homeHref}"${ac('index')}>Home</a>
        <a href="blog.html"${ac('blog')}>Blog</a>
        <a href="community.html"${ac('community')}>Community</a>
        <a href="tutorial.html"${ac('tutorial')}>Tutorial</a>
        <a href="card-game.html"${ac('card-game')}>Game</a>
        <button id="navConnectWallet" class="connect-wallet-btn" onclick="handleNavWalletClick()" style="display:none;">
          <span class="btn-icon-wrap"><img src="/xaman.jpeg" alt="" class="btn-xaman-icon"></span><span class="btn-label">Connect Wallet</span>
        </button>
        <div id="navWalletInfo" class="nav-wallet-info" style="display:none;">
          <a href="dashboard.html" class="wallet-info-link">
            <div class="wallet-balance">
              <span id="navAOXBalance">-- AOX</span>
            </div>
            <div class="wallet-address-short" id="navWalletAddress">--</div>
          </a>
          <button class="disconnect-btn" onclick="handleDisconnect(event)" title="Disconnect Wallet">×</button>
        </div>
      </div>
      <div class="hamburger" onclick="toggleMobileMenu()">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>`;
}

function getMobileMenuHTML(): string {
  const page = getActivePage();
  const isIndex = page === 'index';
  const homeHref = isIndex ? '#home' : 'index.html#home';

  return `<a href="${homeHref}" onclick="toggleMobileMenu()">Home</a>
    <a href="blog.html" onclick="toggleMobileMenu()">Blog</a>
    <a href="community.html" onclick="toggleMobileMenu()">Community</a>
    <a href="tutorial.html" onclick="toggleMobileMenu()">Tutorial</a>
    <a href="card-game.html" onclick="toggleMobileMenu()">Game</a>
    <button id="mobileConnectWallet" class="connect-wallet-btn" onclick="handleNavWalletClick(); toggleMobileMenu();" style="display:none;">
      <span class="btn-icon-wrap"><img src="/xaman.jpeg" alt="" class="btn-xaman-icon"></span><span class="btn-label">Connect Wallet</span>
    </button>
    <div id="mobileWalletInfo" class="mobile-wallet-info" style="display:none;">
      <a href="dashboard.html" onclick="toggleMobileMenu(); window.location.href='dashboard.html'; return false;">
        <span id="mobileAOXBalance" class="mobile-balance">-- AOX</span>
        <span id="mobileWalletAddress" class="mobile-address">--</span>
      </a>
      <button class="disconnect-btn" onclick="handleDisconnect(event); toggleMobileMenu();">×</button>
    </div>`;
}

function getFooterHTML(): string {
  return `<div class="footer-wrapper">
      <div class="footer-brand">
        <div class="footer-logo-container">
          <img src="img/logo.png" alt="AOX Logo" class="footer-logo">
          <h3>Aliens On XRPL</h3>
        </div>
        <p class="footer-tagline">The premier alien NFT collection on the XRP Ledger</p>
        <div class="footer-socials">
          <a href="https://x.com/AliensOnXRPL" class="social-icon" aria-label="X">
            <img src="img/X.png" alt="X">
          </a>
          <a href="https://t.me/aliensonxrpl" class="social-icon" aria-label="Telegram">
            <img src="img/telegram.png" alt="Telegram">
          </a>
          <a href="https://xrp.cafe/collection/aliensonxrpl" class="social-icon" aria-label="XRP Cafe">
            <img src="img/xrpcafe.png" alt="XRP Cafe">
          </a>
          <a href="https://horizonxrpl.com/wallet?ref=o4ReA5wTJA" class="social-icon" aria-label="Horizon">
            <img src="img/horizon-logo.png" alt="Horizon">
          </a>
          <a href="https://dexscreener.com/xrpl/AOX.r3SXB8pVDiUu7DWQEbCJ3ybwZsV7hszPoh_xrp" class="social-icon" aria-label="Dexscreener">
            <img src="img/dexscreener.png" alt="Dexscreener">
          </a>
        </div>
      </div>
      <div class="footer-column">
        <h4>Navigation</h4>
        <a href="index.html#home">Home</a>
        <a href="index.html#about">About</a>
        <a href="index.html#nfts">NFT Collection</a>
        <a href="index.html#token">AOX Token</a>
      </div>
      <div class="footer-column">
        <h4>Community</h4>
        <a href="https://x.com/AliensOnXRPL" target="_blank">X (Twitter)</a>
        <a href="https://t.me/aliensonxrpl" target="_blank">Telegram</a>
      </div>
      <div class="footer-column">
        <h4>Marketplace</h4>
        <a href="https://xrp.cafe/collection/aliensonxrpl" target="_blank">XRP Cafe</a>
        <a href="https://dexscreener.com/xrpl/AOX.r3SXB8pVDiUu7DWQEbCJ3ybwZsV7hszPoh_xrp" target="_blank">DexScreener</a>
        <a href="https://horizonxrpl.com/token/AOX:r3SXB8pVDiUu7DWQEbCJ3ybwZsV7hszPoh?ref=uiQv4T24sF" target="_blank">Horizon DEX</a>
      </div>
      <div class="footer-column">
        <h4>Resources</h4>
        <a href="https://aliensonxrpl.gitbook.io/aox-whitepaper" target="_blank">Whitepaper</a>
        <a href="tutorial.html">How to Play</a>
        <a href="wallet-security.html">Wallet Security</a>
        <a href="card-game.html">Play Game</a>
        <a href="terms.html">Terms of Service</a>
        <a href="privacy.html">Privacy Policy</a>
      </div>
    </div>
    <div class="footer-bottom">
      <div class="footer-bottom-content">
        <p>&copy; 2024&ndash;2026 Aliens On XRPL. All rights reserved.</p>
      </div>
    </div>`;
}

// --- Shared Navigation class ---

interface WalletAuthState {
  address: string | null;
  isConnected: boolean;
}

class SharedNavigation {
  private authState: WalletAuthState = {
    address: null,
    isConnected: false
  };
  private xamanAuth: XamanAuth;

  constructor() {
    this.xamanAuth = new XamanAuth();
  }

  /**
   * Initialize navigation — inject templates then set auth state
   */
  initialize(): void {
    this.injectTemplates();
    this.checkAuthState();
    this.updateNavigation();
  }

  /**
   * Inject navbar / mobile-menu / footer HTML into empty placeholder elements
   */
  private injectTemplates(): void {
    const navbar = document.getElementById('navbar');
    if (navbar && !navbar.innerHTML.trim()) {
      navbar.innerHTML = getNavbarHTML();
    }

    const mobileMenu = document.getElementById('mobileMenu');
    if (mobileMenu && !mobileMenu.innerHTML.trim()) {
      mobileMenu.innerHTML = getMobileMenuHTML();
    }

    const footer = document.getElementById('siteFooter');
    if (footer && !footer.innerHTML.trim()) {
      footer.innerHTML = getFooterHTML();
    }
  }

  /**
   * Check current authentication state
   */
  private checkAuthState(): void {
    const xamanAuth = localStorage.getItem('xamanAuth');
    const walletAddress = localStorage.getItem('xaman_wallet_address');

    if (xamanAuth) {
      try {
        const auth = JSON.parse(xamanAuth);
        if (auth.address) {
          this.authState.address = auth.address;
          this.authState.isConnected = true;
        }
      } catch (error) {
        console.error('Error parsing xamanAuth:', error);
      }
    } else if (walletAddress) {
      this.authState.address = walletAddress;
      this.authState.isConnected = true;
    }
  }

  /**
   * Update navigation based on auth state
   */
  private updateNavigation(): void {
    const navConnectBtn = document.getElementById('navConnectWallet');
    const navWalletInfo = document.getElementById('navWalletInfo');
    const mobileConnectBtn = document.getElementById('mobileConnectWallet');
    const mobileWalletInfo = document.getElementById('mobileWalletInfo');

    if (this.authState.isConnected && this.authState.address) {
      if (navConnectBtn) navConnectBtn.style.display = 'none';
      if (mobileConnectBtn) mobileConnectBtn.style.display = 'none';
      if (navWalletInfo) navWalletInfo.style.display = 'flex';
      if (mobileWalletInfo) mobileWalletInfo.style.display = 'flex';

      const navAddress = document.getElementById('navWalletAddress');
      const mobileAddress = document.getElementById('mobileWalletAddress');

      if (navAddress) navAddress.textContent = this.shortenAddress(this.authState.address);
      if (mobileAddress) mobileAddress.textContent = this.shortenAddress(this.authState.address);

      this.fetchAOXBalance(this.authState.address);
    } else {
      if (navConnectBtn) navConnectBtn.style.display = 'block';
      if (mobileConnectBtn) mobileConnectBtn.style.display = 'block';
      if (navWalletInfo) navWalletInfo.style.display = 'none';
      if (mobileWalletInfo) mobileWalletInfo.style.display = 'none';
    }
  }

  private createWalletButton(address: string): HTMLElement {
    const container = document.createElement('div');
    container.className = 'nav-wallet-info';
    container.innerHTML = `
      <a href="dashboard.html" class="wallet-info-link">
        <div class="wallet-balance">
          <span id="navAOXBalance">Loading...</span>
        </div>
        <div class="wallet-address-short">${this.shortenAddress(address)}</div>
      </a>
    `;
    this.fetchAOXBalance(address);
    return container;
  }

  private async fetchAOXBalance(address: string): Promise<void> {
    try {
      const cacheKey = `balance_${address}`;
      const cached = this.getCachedData(cacheKey, 30 * 1000);

      let balanceText: string;
      let balanceValue: string = '0';

      if (cached) {
        balanceText = `${cached} AOX`;
        balanceValue = cached;
      } else {
        const response = await fetch(`${API_BASE_URL}/xaman/balance/${address}`);
        const data = await response.json();

        if (data.success) {
          this.setCachedData(cacheKey, data.balance);
          balanceText = `${data.balance} AOX`;
          balanceValue = data.balance;
        } else {
          balanceText = '-- AOX';
        }
      }

      localStorage.setItem(`aox_balance_${address}`, balanceValue);

      const navBalance = document.getElementById('navAOXBalance');
      const mobileBalance = document.getElementById('mobileAOXBalance');
      if (navBalance) navBalance.textContent = balanceText;
      if (mobileBalance) mobileBalance.textContent = balanceText;
    } catch (error) {
      console.error('Error fetching balance:', error);
      const navBalance = document.getElementById('navAOXBalance');
      const mobileBalance = document.getElementById('mobileAOXBalance');
      if (navBalance) navBalance.textContent = '-- AOX';
      if (mobileBalance) mobileBalance.textContent = '-- AOX';
    }
  }

  private getCachedData(key: string, maxAge: number): any | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age > maxAge) {
        localStorage.removeItem(key);
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  }

  private setCachedData(key: string, data: any): void {
    try {
      const cacheObj = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(cacheObj));
    } catch (error) {
      console.error('Cache write error:', error);
    }
  }

  private createMobileWalletButton(address: string): HTMLElement {
    const button = document.createElement('a');
    button.href = 'dashboard.html';
    button.onclick = () => {
      const mobileMenu = document.getElementById('mobileMenu');
      mobileMenu?.classList.remove('active');
      const hamburger = document.querySelector('.hamburger');
      hamburger?.classList.remove('active');
    };
    button.innerHTML = `👽 ${this.shortenAddress(address)}`;
    return button;
  }

  private shortenAddress(address: string): string {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  }

  async handleWalletConnect(): Promise<void> {
    try {
      const result = await this.xamanAuth.login();

      if (result.success && result.address) {
        this.authState.address = result.address;
        this.authState.isConnected = true;
        this.updateNavigation();

        if (typeof window.initializeGlobalChat === 'function') {
          window.initializeGlobalChat();
        }
      } else {
        showError(result.error || 'Failed to connect wallet');
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      showError('Failed to connect with Xaman. Please try again.');
    }
  }

  handleDisconnect(): void {
    this.xamanAuth.logout();
    this.authState.address = null;
    this.authState.isConnected = false;
    this.updateNavigation();
    window.location.reload();
  }
}

// Initialize on page load
const sharedNav = new SharedNavigation();
document.addEventListener('DOMContentLoaded', () => {
  sharedNav.initialize();
});

// Global functions for onclick handlers
function handleNavWalletClick(): void {
  sharedNav.handleWalletConnect();
}

function handleDisconnect(event?: Event): void {
  if (event) {
    event.stopPropagation();
  }
  sharedNav.handleDisconnect();
}

function toggleMobileMenu(): void {
  const hamburger = document.querySelector('.hamburger');
  const mobileMenu = document.getElementById('mobileMenu');
  hamburger?.classList.toggle('active');
  mobileMenu?.classList.toggle('active');
}

// Make available globally
declare global {
  interface Window {
    sharedNav: SharedNavigation;
    handleNavWalletClick: typeof handleNavWalletClick;
    handleDisconnect: typeof handleDisconnect;
    toggleMobileMenu: typeof toggleMobileMenu;
  }
}

window.sharedNav = sharedNav;
window.handleNavWalletClick = handleNavWalletClick;
window.handleDisconnect = handleDisconnect;
window.toggleMobileMenu = toggleMobileMenu;
